import React from 'react';
import { useNavigate } from 'react-router';
import { CircularProgress, Grid, Typography } from '@material-ui/core';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { PrimaryButton } from '@/styles/blocks';
import { PrimaryHeader } from '@/styles/typography';
import {
    OnwardBreadcrumbActive,
    OnwardBreadcrumbInactive,
    OnwardBreadcrumbSpacer,
} from '@/components/Breadcrumbs';
import { CYCLE_COUNT_STATUS_LABELS, STATUS_NEUTRAL } from './constants';

const StatusChipRoot = styled.span`
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    color: white;
    background-color: ${(props) => props.color};
`;

export const StatusChip = ({ status }) => {
    const cfg = CYCLE_COUNT_STATUS_LABELS[status] || { label: status || '-', color: STATUS_NEUTRAL };
    return <StatusChipRoot color={cfg.color}>{cfg.label}</StatusChipRoot>;
};

export const DetailLine = ({ label, value }) => {
    const isElement = React.isValidElement(value);
    return (
        <div
            css={css`
                display: flex;
                align-items: center;
                gap: 0.35rem;
                padding: 4px 0;
                font-family: 'Montserrat', 'Roboto', 'Arial';
                color: #000;
                font-size: 14px;
                text-align: left;
            `}
        >
            <span>{label}:</span>
            <span>{isElement ? value : value || '-'}</span>
        </div>
    );
};

// Full-height centered loader shared by the detail pages.
export const CenteredSpinner = ({ size = 60 }) => (
    <Grid
        container
        css={css`
            height: 100%;
            justify-content: center;
            align-items: center;
        `}
    >
        <CircularProgress color="primary" size={size} />
    </Grid>
);

export const DetailNotFound = ({ title, message }) => (
    <Grid
        container
        direction="column"
        css={css`
            padding: 2rem;
            gap: 1rem;
        `}
    >
        <PrimaryHeader>{title}</PrimaryHeader>
        <Typography>{message}</Typography>
    </Grid>
);

// Bordered breadcrumb header row used by the simple detail pages.
export const DetailHeaderBar = styled(Grid)`
    padding: 1rem 1.5rem;
    border-bottom: 1px solid ${colors.greys.border};
    background: ${colors.white.primary};
    align-items: center;
`;

// Renders a breadcrumb trail. Each crumb is { label, to? }; crumbs before the
// last are clickable (navigating to `to` when present), the last is active.
export const CycleCountBreadcrumbs = ({ trail = [] }) => {
    const navigate = useNavigate();
    return (
        <>
            {trail.map((crumb, index) => {
                const isLast = index === trail.length - 1;
                return (
                    <React.Fragment key={`${crumb.label}-${index}`}>
                        {isLast ? (
                            <OnwardBreadcrumbActive>{crumb.label}</OnwardBreadcrumbActive>
                        ) : (
                            <OnwardBreadcrumbInactive onClick={() => crumb.to && navigate(crumb.to)}>
                                {crumb.label}
                            </OnwardBreadcrumbInactive>
                        )}
                        {isLast ? null : <OnwardBreadcrumbSpacer />}
                    </React.Fragment>
                );
            })}
        </>
    );
};

// Footer action button — shared spacing/height for the CycleCountItem action row.
export const FooterActionButton = styled(PrimaryButton)`
    margin-left: 1rem;
    height: 44px;
`;
